import { FC } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { EditableColumn, EditableDataTable } from '@/components/ui';
import { SchedulerRun } from '../types';
import {
    ChevronRight,
    AlertCircle,
    X,
    Loader2
} from 'lucide-react';

export const SchedulerLogsTable: FC<{
    onRefreshRequest?: (refreshFn: () => void) => void;
}> = ({ onRefreshRequest }) => {
    const [tableData, setTableData] = useState<SchedulerRun[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRun, setSelectedRun] = useState<SchedulerRun | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    const { addToast } = useToast();

    const fetchLogs = useCallback(
        async (page: number, limit: number, query: string) => {
            try {
                setLoading(true);
                const params: Record<string, string | number> = { page, limit };
                if (query) params.type = query;

                const res = await api.get('/idm/rss-scheduler/runs', { params });
                setTableData(res.data.data || []);
                setTotalCount(res.data.total || 0);
            } catch (err) {
                console.error('❌ Failed to fetch scheduler logs:', err);
                addToast('Failed to load scheduler logs', 'error');
            } finally {
                setLoading(false);
            }
        },
        [addToast]
    );

    useEffect(() => {
        fetchLogs(currentPage, pageSize, searchQuery);
    }, [currentPage, pageSize, searchQuery, fetchLogs]);

    const handleRefresh = useCallback(() => {
        fetchLogs(currentPage, pageSize, searchQuery);
    }, [fetchLogs, currentPage, pageSize, searchQuery]);

    // Pass the refresh function up to the parent if requested
    useEffect(() => {
        if (onRefreshRequest) {
            onRefreshRequest(handleRefresh);
        }
    }, [onRefreshRequest, handleRefresh]);

    const handleGlobalSearch = async (query?: string) => {
        const q = String(query ?? '');
        setSearchQuery(q);
        setCurrentPage(1);
    };

    const openDetails = (run: SchedulerRun) => {
        setSelectedRun(run);
        // Delay opening state to trigger CSS transition after mounting
        setTimeout(() => setIsDetailsOpen(true), 10);
    };

    const closeDetails = () => {
        setIsDetailsOpen(false);
        setTimeout(() => setSelectedRun(null), 300);
    };

    const formatDuration = (seconds?: number) => {
        if (seconds === undefined || seconds === null) return '-';
        if (seconds < 1) return `${(seconds * 1000).toFixed(0)} ms`;
        return `${seconds.toFixed(2)} s`;
    };

    const getStatusConfig = (status: string) => {
        switch (status) {
            case 'completed':
                return {
                    icon: null,
                    text: 'Completed',
                    style: 'text-emerald-600 font-medium',
                };
            case 'failed':
                return {
                    icon: null,
                    text: 'Failed',
                    style: 'text-rose-600 font-medium',
                };
            case 'running':
                return {
                    icon: <Loader2 className="w-3 h-3 animate-spin mr-1.5" />,
                    text: 'Running',
                    style: 'text-blue-600 font-medium',
                };
            default:
                return {
                    icon: null,
                    text: status,
                    style: 'text-slate-500 font-medium',
                };
        }
    };

    const renderStatus = (status: string) => {
        const config = getStatusConfig(status);
        return (
            <div className={`flex items-center text-xs ${config.style}`}>
                {config.icon}
                {config.text}
            </div>
        );
    };

    const getTypeConfig = (type: string) => {
        const lower = type.toLowerCase();
        if (lower.includes('mxm')) return { label: 'Post Idea Creation' };
        if (lower.includes('idm')) return { label: 'Rss Feed Analysis' };
        if (lower.includes('linkedin')) return { label: 'Competitor Post Analysis' };
        if (lower.includes('rss')) return { label: 'RSS Feed' };
        if (lower.includes('db') || lower.includes('database')) return { label: 'Database' };
        if (lower.includes('process')) return { label: 'Processing' };
        return { label: type.charAt(0).toUpperCase() + type.slice(1).toLowerCase() };
    };

    const columns: EditableColumn<SchedulerRun>[] = [
        {
            key: 'runId',
            header: 'ID',
            editable: false,
            render: (row) => (
                <span className="text-[11px] font-mono text-slate-400">
                    {row.runId.substring(0, 8)}
                </span>
            )
        },
        {
            key: 'type',
            header: 'Automation Worker',
            editable: false,
            render: (row) => {
                const config = getTypeConfig(row.type);
                return (
                    <span className="text-sm text-slate-700 font-medium">{config.label}</span>
                );
            }
        },
        {
            key: 'status',
            header: 'Execution Status',
            editable: false,
            render: (row) => renderStatus(row.status),
        },
        {
            key: 'startedAt',
            header: 'Triggered At',
            editable: false,
            render: (row) => (
                <span className="text-xs text-slate-500">
                    {new Date(row.startedAt).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </span>
            ),
        },
        {
            key: 'durationSeconds',
            header: 'Latency',
            editable: false,
            render: (row) => (
                <span className="text-xs text-slate-500 font-mono">
                    {formatDuration(row.durationSeconds)}
                </span>
            ),
        },
        {
            key: 'metrics',
            header: 'Workload',
            editable: false,
            render: (row) => {
                const m = row.metrics || {};
                const count = Number(m.totalPostsGenerated || 0) +
                    Number(m.totalCompaniesProcessed || 0) +
                    Number(m.articlesScraped || 0) +
                    Number(m.topicsGenerated || 0);
                return (
                    <span className="text-xs font-medium text-slate-600">
                        {count > 0 ? `${count} items` : '-'}
                    </span>
                );
            }
        },
        {
            key: 'errorMessage',
            header: '',
            editable: false,
            render: (row) => (
                <div className="flex justify-end">
                    <button
                        onClick={() => openDetails(row)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-bold text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-50 hover:border-slate-300 hover:text-slate-900 transition-all shadow-sm active:bg-slate-100"
                    >
                        DETAILS
                        <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="animate-in fade-in duration-500">
            <EditableDataTable<SchedulerRun>
                title=""
                columns={columns}
                data={tableData}
                enableEditing={false}
                enableDelete={false}
                className="border-0 shadow-none bg-transparent"
                onDataUpdate={setTableData}
                onGlobalSearch={handleGlobalSearch}
                pagination={{
                    pageIndex: currentPage - 1,
                    pageSize,
                    totalCount,
                }}
                onPaginationChange={({ pageIndex, pageSize: nextSize }) => {
                    const sizeChanged = nextSize !== pageSize;
                    setPageSize(nextSize);
                    setCurrentPage(sizeChanged ? 1 : pageIndex + 1);
                }}
                loading={loading}
                globalFilterValue={searchQuery}
            />


            {/* Details Side Sheet */}
            {selectedRun && (
                <div
                    className={`fixed inset-0 z-[1001] transition-opacity duration-300 ${isDetailsOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                >
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                        onClick={closeDetails}
                    />

                    {/* Sliding Panel */}
                    <div
                        className={`absolute top-0 right-0 w-full max-w-lg h-full bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${isDetailsOpen ? 'translate-x-0' : 'translate-x-full'}`}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="px-6 py-6 border-b border-slate-800 bg-slate-900 sticky top-0 z-10">
                            <div className="flex items-center gap-2 mb-2">
                                <div className={`w-2 h-2 rounded-full ${selectedRun.status === 'completed' ? 'bg-emerald-500' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]'}`} />
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{getTypeConfig(selectedRun.type).label} execution</span>
                            </div>
                            <div className="flex items-start justify-between">
                                <h3 className="text-xl font-bold text-slate-50 tracking-tight">Run Details</h3>
                                <button
                                    onClick={closeDetails}
                                    className="text-slate-500 hover:text-slate-300 p-1.5 hover:bg-slate-800 rounded-lg transition-all"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="mt-4 flex items-center gap-3">
                                <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-[10px] font-mono text-slate-400">
                                    ID: {selectedRun.runId.substring(0, 12)}...
                                </span>
                                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-tight">
                                    {new Date(selectedRun.startedAt).toLocaleDateString()} at {new Date(selectedRun.startedAt).toLocaleTimeString()}
                                </span>
                            </div>
                        </div>

                        {/* Content Body */}
                        <div className="flex-1 overflow-y-auto bg-slate-950 custom-scrollbar scrollbar-dark">
                            <div className="p-6 space-y-10">
                                {/* Execution Stats - Integrated Row */}
                                <section className="grid grid-cols-2 gap-4">
                                    <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Status</p>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-sm font-bold capitalize ${selectedRun.status === 'completed' ? 'text-emerald-400' : 'text-rose-400'}`}>
                                                {selectedRun.status}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="bg-slate-900/50 border border-slate-800/50 rounded-xl p-4">
                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Duration</p>
                                        <div className="text-sm font-bold text-slate-50 font-mono">{formatDuration(selectedRun.durationSeconds)}</div>
                                    </div>
                                </section>

                                {/* Context Section */}
                                <section>
                                    <h4 className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.2em] mb-4">Context & Source</h4>
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between py-2 border-b border-slate-900">
                                            <span className="text-xs text-slate-500 font-medium">Trigger Source</span>
                                            <span className="text-xs text-slate-200 font-bold">{selectedRun.triggeredBy || 'System Scheduler'}</span>
                                        </div>
                                        <div className="flex items-center justify-between py-2 border-b border-slate-900">
                                            <span className="text-xs text-slate-500 font-medium">Execution Type</span>
                                            <span className="text-xs text-slate-200 font-bold capitalize">{getTypeConfig(selectedRun.type).label}</span>
                                        </div>
                                    </div>
                                </section>

                                {/* Metrics Section */}
                                {selectedRun.metrics && Object.keys(selectedRun.metrics).length > 0 && (
                                    <section>
                                        <h4 className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.2em] mb-6">Execution Metrics</h4>
                                        <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                                            {Object.entries(selectedRun.metrics).map(([key, value]) => {
                                                const isArrayOfObjects = Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null;
                                                const isLongValue = key === 'noPostsCompaniesList' || isArrayOfObjects || (Array.isArray(value) && value.length > 5) || String(value).length > 40;

                                                return (
                                                    <div key={key} className={`group ${isLongValue ? 'col-span-2' : ''}`}>
                                                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-tight mb-1 group-hover:text-blue-400 transition-colors">
                                                            {key.replace(/_/g, ' ')}
                                                        </div>

                                                        {isArrayOfObjects ? (
                                                            <div className="mt-2 overflow-x-auto rounded-xl border border-slate-800/60 bg-slate-900/40 backdrop-blur-sm">
                                                                <table className="w-full text-left text-[10px] border-collapse min-w-[300px]">
                                                                    <thead>
                                                                        <tr className="bg-slate-800/40 border-b border-slate-800">
                                                                            {Object.keys(value[0]).map(k => (
                                                                                <th key={k} className="px-3 py-2 font-bold text-slate-500 uppercase tracking-wider">
                                                                                    {k.replace(/_/g, ' ')}
                                                                                </th>
                                                                            ))}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-slate-800/30">
                                                                        {value.map((item: any, idx: number) => (
                                                                            <tr key={idx} className="hover:bg-slate-800/20 transition-colors">
                                                                                {Object.keys(value[0]).map(k => (
                                                                                    <td key={k} className="px-3 py-2 text-slate-300 font-medium">
                                                                                        {typeof item[k] === 'object' ? JSON.stringify(item[k]) : String(item[k])}
                                                                                    </td>
                                                                                ))}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        ) : (
                                                            <div className="text-sm font-bold text-slate-50 font-mono break-words whitespace-pre-wrap leading-relaxed">
                                                                {Array.isArray(value)
                                                                    ? value.map(item => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ')
                                                                    : (typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </section>
                                )}

                                {/* Error Alert */}
                                {selectedRun.errorMessage && (
                                    <section className="animate-in fade-in slide-in-from-top-2 duration-500 pt-4">
                                        <div className="bg-rose-500/10 border border-rose-500/20 rounded-xl p-5 overflow-hidden relative">
                                            <div className="absolute top-0 right-0 p-3 opacity-10">
                                                <AlertCircle className="w-8 h-8 text-rose-500" />
                                            </div>
                                            <h4 className="text-[10px] font-bold text-rose-400 uppercase tracking-widest mb-3">Error Traceback</h4>
                                            <div className="max-h-[200px] overflow-y-auto custom-scrollbar-red">
                                                <pre className="text-[11px] font-mono text-rose-200/80 whitespace-pre-wrap break-words leading-relaxed">
                                                    {selectedRun.errorMessage}
                                                </pre>
                                            </div>
                                        </div>
                                    </section>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
